class TicketRedeemer {
    /**
     * @param {DialogService} dialogService
     * @param {RedemptionService} redemptionService
     * @param {UserService} userService
     */
    constructor(
        dialogService,
        redemptionService,
        userService,
    ) {
        this.dialogService = dialogService;
        this.redemptionService = redemptionService;
        this.userService = userService;
    }

    /**
     * Check if any of the given lines are for a different branch.
     * If they are, check if the crossBranchRedemptionConfirmed has been set on the line.
     * If it has not, show a dialog to the user asking them to confirm the cross branch redemption.
     * If the user declines that, return true.
     *
     * @param {OrderLine[]} lines
     * @param {boolean} [isUnredemption]
     * @param {ItemInstance|null} [instance]
     *
     * @return {Promise<boolean>}
     */
    async isUnconfirmedCrossBranchRedemption(
        lines,
        isUnredemption = false,
        instance = null
    ) {
        // We'll only do this check if the company has cross branch selling enabled.
        // It's possible some clients are doing weird cross branch stuff and the confirmation would be annoying
        // for them.
        // We could also just remove this check when cross branch selling goes live and start showing for everyone,
        // so it also works as a "disable until we release it" check.
        if (!this.userService.company.settings.enableCrossBranchSelling) {
            return false;
        }

        // Check if any of these lines are from another branch.
        let qtyFromAnotherBranch = 0;
        let qtyFromSameBranch = 0;
        let hasUnconfirmedLinesFromAnotherBranch = false;

        lines.forEach((line) => {
            if (line.branchID === this.userService.getOrderBranchID()) {
                qtyFromSameBranch += line.getQuantity();
            } else {
                qtyFromAnotherBranch += line.getQuantity();
                if (!line.crossBranchRedemptionConfirmed) {
                    hasUnconfirmedLinesFromAnotherBranch = true;
                }
            }
        });

        if (!hasUnconfirmedLinesFromAnotherBranch) {
            // No foreign lines.
            return false;
        }

        // Show a confirm modal.
        // If they accept we will mark all these lines as "crossBranchRedemptionConfirmed" and return false.

        // Get the wording of the message right depending on the number of tickets.
        let warningMessage;
        if (instance) {
            // Redeeming a single instance.
            warningMessage = 'REDEMPTION.REDEEM_DIFFERENT_BRANCH_WARNING_ONE';
        } else if (qtyFromAnotherBranch === 1 && qtyFromSameBranch === 0) {
            // The lines contain exactly one ticket and it is from another branch.
            warningMessage = 'REDEMPTION.REDEEM_DIFFERENT_BRANCH_WARNING_ONE';
        } else if (qtyFromAnotherBranch > 0 && qtyFromSameBranch === 0) {
            // The lines contain more than one ticket and they are all from another branch.
            warningMessage = 'REDEMPTION.REDEEM_DIFFERENT_BRANCH_WARNING_MANY';
        } else {
            // The lines contain a mixture of cross branch and non cross branch tickets.
            warningMessage = 'REDEMPTION.REDEEM_DIFFERENT_BRANCH_WARNING_SOME';
        }

        const confirmed = await this.dialogService.confirm(
            [
                warningMessage,
                isUnredemption
                    ? 'REDEMPTION.REDEEM_DIFFERENT_BRANCH_UNREDEEM'
                    : 'REDEMPTION.REDEEM_DIFFERENT_BRANCH_REDEEM',
            ]
        );
        if (!confirmed) {
            // The user declined, so this is still an unconfirmed cross branch redemption. Prevent it.
            return true;
        }

        // The user has confirmed they want to redeem these cross branch lines.
        // Remember that on the lines.
        lines.forEach((line) => {
            line.crossBranchRedemptionConfirmed = true;
        });

        // It's no longer unconfirmed.
        return false;
    }

    /**
     * Is there one or more lines in the given array of lines that has unallocated seats,
     * and where that line has not been marked as confirmed for redemption?
     *
     * @param {OrderLine[]} lines
     *
     * @returns {Promise<boolean>}
     */
    async isUnconfirmedUnallocatedSeatRedemption(lines) {
        let hasUnallocatedSeats = false;
        for (const line of lines) {
            if (line.unallocatedSeatCount > 0 && !line.unallocatedSeatRedemptionConfirmed) {
                hasUnallocatedSeats = true;
                break;
            }
        }

        if (!hasUnallocatedSeats) {
            // There are no unconfirmed unallocated seats.
            return false;
        }

        const confirmed = await this.dialogService.confirm(
            'REDEMPTION.REDEEM_UNALLOCATED_SEATS',
        );

        if (!confirmed) {
            // The user declined, so this is still an unconfirmed unallocated seat redemption. Prevent it.
            return true;
        }

        // The user has confirmed they want to redeem these unallocated seats.
        // Remember that on the lines.
        lines.forEach((line) => {
            line.unallocatedSeatRedemptionConfirmed = true;
        });

        // It's no longer unconfirmed.
        return false;
    }

    /**
     * @param {Order} order
     * @param {OrderLine[]} lines
     */
    redeemLines(order, lines) {
        lines.forEach((orderLine) => {
            if (!orderLine.isRedemptionAllowed()) {
                return;
            }

            if (orderLine.hasIndividualTicketInstances()) {
                orderLine.iteminstances.forEach((itemInstance) => {
                    if (itemInstance.netRedemptions === 0 && !itemInstance.redemptionIsInProgress()) {
                        this.redeemItemInstance(order, itemInstance, false);
                    }
                });
            } else if (orderLine.redeemed < orderLine.qty && !orderLine.redemptionIsInProgress()) {
                this.redeemAllRemainingOnLine(order, orderLine, undefined, false);
            }
        });

        // One broadcast for the whole batch, rather than one per line/instance dispatched above.
        this.redemptionService.notifyRedemptionActivity();
    }

    /**
     * @param {Order} order
     * @param {OrderLine[]} lines
     */
    unredeemLines(order, lines) {
        lines.forEach((orderLine) => {
            if (!orderLine.isRedemptionAllowed()) {
                return;
            }

            if (orderLine.hasIndividualTicketInstances()) {
                orderLine.iteminstances.forEach((itemInstance) => {
                    if (itemInstance.netRedemptions > 0 && !itemInstance.redemptionIsInProgress()) {
                        this.unredeemItemInstance(order, itemInstance, false);
                    }
                });
            } else if (orderLine.redeemed >= orderLine.qty && !orderLine.redemptionIsInProgress()) {
                this.unredeemAllOnLine(order, orderLine, false);
            }
        });

        // One broadcast for the whole batch, rather than one per line/instance dispatched above.
        this.redemptionService.notifyRedemptionActivity();
    }

    /**
     * @param {Order} order
     * @param {ItemInstance} itemInstance
     * @param {boolean} [notify]
     */
    redeemItemInstance(order, itemInstance, notify = true) {
        this.redemptionService
            .redeemItemInstance(
                order,
                itemInstance,
                1,
                notify
            );
    }

    /**
     * @param {Order} order
     * @param {ItemInstance} itemInstance
     * @param {boolean} [notify]
     */
    unredeemItemInstance(order, itemInstance, notify = true) {
        this.redemptionService
            .redeemItemInstance(
                order,
                itemInstance,
                -1,
                notify
            );
    }

    /**
     * @param {Order} order
     * @param {OrderLine} line
     * @param {number} qty How much of the line to redeem. Can be negative for unredemption.
     * @param {string} [method]
     * @param {boolean} [notify] Broadcast that redemption activity has started immediately.
     *   Pass false when called as part of a batch (e.g. redeemLines/unredeemLines) which will
     *   broadcast once itself after dispatching the whole batch, rather than once per line.
     */
    redeemQtyOnLine(order, line, qty, method, notify = true) {
        // Set the direction-aware flag immediately (rather than always assuming redemption and
        // relying on RedemptionService to correct it once its own debounce settles) so the
        // correct icon shows from the moment this is called, not ~100ms later.
        if (qty > 0) {
            line.setRedemptionInProgress();
        } else if (qty < 0) {
            line.setUnredemptionInProgress();
        }

        let newTotal = parseInt(line.getRedemptionCount(), 10) + qty;
        if (newTotal < 0 || newTotal > parseInt(line.getQuantity(), 10)) {
            line.clearRedemptionInProgress();
            return;
        }

        if (notify) {
            this.redemptionService.notifyRedemptionActivity();
        }

        this.redemptionService.adjustRedemptionCount(
            order,
            line,
            qty,
            method
        );
    }

    /**
     * @param {Order} order
     * @param {OrderLine} line
     * @param {string} [method]
     * @param {boolean} [notify]
     */
    redeemAllRemainingOnLine(order, line, method, notify = true) {
        let qty = parseInt(line.getQuantity(), 10) - parseInt(line.getRedemptionCount(), 10);
        this.redeemQtyOnLine(order, line, qty, method, notify);
    }

    /**
     * @param {Order} order
     * @param {OrderLine} line
     * @param {boolean} [notify]
     */
    unredeemAllOnLine(order, line, notify = true) {
        let qty = parseInt(line.getRedemptionCount(), 10);
        this.redeemQtyOnLine(order, line, -qty, undefined, notify);
    }
}

module.exports = TicketRedeemer;
